import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'

const API = 'https://pkjjdiedczbkbcxuhonn.supabase.co/functions/v1/qr-order'
const money = (n) => `R${Number(n || 0).toFixed(2)}`

function getToken() {
  const m = window.location.pathname.match(/\/t\/([0-9a-f-]{36})/i)
  if (m) return m[1]
  return new URLSearchParams(window.location.search).get('t') ?? ''
}

// This phone's identity at the table. It is not a login and it is not a
// tracking id: it exists so two people on one table can each be told apart
// while sharing a single tab.
function getDeviceKey() {
  let k = localStorage.getItem('wr-order-device')
  if (!k || !/^[0-9a-f-]{36}$/i.test(k)) {
    k = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`.padEnd(36, '0').slice(0, 36))
    localStorage.setItem('wr-order-device', k)
  }
  return k
}

// POPIA: The customer's first name is stored in localStorage so they don't have to
// re-enter it if they reload the page during their visit.  We clear it when a new
// table token is detected — i.e. when the customer has moved to a different table
// or a different restaurant — to avoid names accumulating across sessions.
function getAndValidateName(currentToken) {
  const storedToken = localStorage.getItem('wr-order-token')
  if (storedToken && storedToken !== currentToken) {
    // Different table/session — clear any previously stored name
    localStorage.removeItem('wr-order-name')
    localStorage.removeItem('wr-order-token')
  }
  if (currentToken) {
    localStorage.setItem('wr-order-token', currentToken)
  }
  return localStorage.getItem('wr-order-name') ?? ''
}


/* ---------------- modifier picker ---------------- */
function ModifierSheet({ item, onAdd, onClose }) {
  const [choices, setChoices] = useState({})
  const [notes, setNotes] = useState('')
  const groups = item.modifier_groups ?? []

  function toggle(g, opt) {
    setChoices(prev => {
      const cur = prev[g.id] ?? []
      if (cur.includes(opt.id)) return { ...prev, [g.id]: cur.filter(x => x !== opt.id) }
      const next = g.max_selections === 1 ? [opt.id] : [...cur, opt.id].slice(0, g.max_selections)
      return { ...prev, [g.id]: next }
    })
  }
  const selectedIds = groups.flatMap(g => choices[g.id] ?? [])
  const selected = groups.flatMap(g => (g.modifier_options ?? []).filter(o => (choices[g.id] ?? []).includes(o.id)))
  const missingRequired = groups.some(g => g.is_required && !(choices[g.id]?.length))
  const total = Number(item.price) + selected.reduce((s, o) => s + Number(o.price_adjustment), 0)

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sheet" style={{ maxWidth: 420 }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: 10 }}>{item.name}</h2>
        {groups.map(g => (
          <div key={g.id} style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
              {g.name}{g.is_required ? ' *' : ''}
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {(g.modifier_options ?? []).filter(o => o.is_active).map(o => (
                <button key={o.id} className={`btn btn-sm ${(choices[g.id] ?? []).includes(o.id) ? 'btn-primary' : 'btn-secondary'}`} onClick={() => toggle(g, o)}>
                  {o.name}{Number(o.price_adjustment) !== 0 ? ` +${money(o.price_adjustment)}` : ''}
                </button>
              ))}
            </div>
          </div>
        ))}
        <input className="input" style={{ marginBottom: 10 }} placeholder="Notes (e.g. no onion)" value={notes} onChange={e => setNotes(e.target.value)} />
        <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={missingRequired}
          onClick={() => onAdd(selectedIds, selected, notes.trim())}>
          Add to order · {money(total)}
        </button>
      </div>
    </div>
  )
}

/* ---------------- ask for the bill, with a tip if they want ---------------- */
function BillSheet({ tabTotal, onSend, onClose, busy }) {
  const [tip, setTip] = useState(0)
  const pct = (p) => Math.round(Number(tabTotal) * p) / 100 * 1
  const presets = [0, 10, 12.5, 15]
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && !busy && onClose()}>
      <div className="sheet" style={{ maxWidth: 420 }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: 4 }}>Ask for the bill</h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
          Table total {money(tabTotal)}. Add a tip if you would like to, or leave it and settle at the table.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
          {presets.map(p => {
            const amt = p === 0 ? 0 : Math.round(Number(tabTotal) * p) / 100
            return (
              <button key={p} className={`btn btn-sm ${Number(tip) === amt ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setTip(amt)}>
                {p === 0 ? 'No tip' : `${p}%`}{p !== 0 && ` · ${money(amt)}`}
              </button>
            )
          })}
        </div>
        <div className="field">
          <label>Or enter an amount</label>
          <input className="input" type="number" min="0" step="1" inputMode="decimal"
            value={tip || ''} onChange={e => setTip(Number(e.target.value) || 0)} placeholder="0" />
        </div>
        <div className="cart-total-row grand" style={{ padding: '10px 4px' }}>
          <span>To pay</span><span>{money(Number(tabTotal) + Number(tip || 0))}</span>
        </div>
        <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={busy} onClick={() => onSend(Number(tip || 0))}>
          {busy ? <span className="spinner" /> : 'Ask for the bill'}
        </button>
      </div>
    </div>
  )
}

export default function App() {
  const token = useMemo(getToken, [])
  const deviceKey = useMemo(getDeviceKey, [])
  const [data, setData] = useState(undefined)
  const [error, setError] = useState(null)
  const [view, setView] = useState('menu')          // menu | tab
  const [cart, setCart] = useState([])
  // POPIA: Use getAndValidateName to clear the stored name when a new table token is detected.
  const [name, setName] = useState(() => getAndValidateName(token))
  const [joined, setJoined] = useState(() => !!getAndValidateName(token))
  const [nameDraft, setNameDraft] = useState('')
  const [picking, setPicking] = useState(null)
  const [busy, setBusy] = useState(false)
  const [confirmed, setConfirmed] = useState(null)
  const [group, setGroup] = useState('all')          // all | food | bar
  const [cat, setCat] = useState('all')
  const [billOpen, setBillOpen] = useState(false)
  const [flash, setFlash] = useState(null)
  const catRowRef = useRef(null)

  const load = useCallback(async () => {
    if (!token) { setError('Scan the QR code on your table to order.'); setData(null); return }
    try {
      const res = await fetch(`${API}?token=${token}`)
      const body = await res.json()
      if (!res.ok) { setError(body.error ?? 'Something went wrong.'); setData(null); return }
      setData(body); setError(null)
    } catch {
      setError('No connection. Check your signal and try again.'); setData(null)
    }
  }, [token])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    const iv = setInterval(load, 15000)   // keep the shared tab fresh
    return () => clearInterval(iv)
  }, [load])

  async function post(payload) {
    const res = await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, device_key: deviceKey, customer_name: name.trim(), ...payload }),
    })
    const body = await res.json()
    if (!res.ok) throw new Error(body.error ?? 'Something went wrong.')
    return body
  }

  // Everyone at the table shares one tab, but each phone says who it is first,
  // so the kitchen and the bill can tell Jan's steak from James's.
  async function join(e) {
    e?.preventDefault()
    const n = nameDraft.trim()
    if (!n || busy) return
    setBusy(true)
    try {
      localStorage.setItem('wr-order-name', n)
      setName(n)
      const res = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action: 'join', device_key: deviceKey, customer_name: n }),
      })
      const body = await res.json()
      if (res.ok) setData(d => ({ ...d, ...body }))
      setJoined(true)
    } catch { setJoined(true) }   // let them order even if the roster write failed
    setBusy(false)
  }

  function addItem(item, modifier_option_ids = [], mods = [], notes = '') {
    setCart(prev => [...prev, {
      lineId: `${Date.now()}-${Math.random()}`,
      product_id: item.id, name: item.name,
      unit: Number(item.price) + mods.reduce((s, m) => s + Number(m.price_adjustment), 0),
      modNames: mods.map(m => m.name), modifier_option_ids, notes, qty: 1,
    }])
    setPicking(null)
  }

  function tryAdd(item) {
    if (item.orderable === false) return
    if ((item.modifier_groups ?? []).some(g => (g.modifier_options ?? []).length)) setPicking(item)
    else addItem(item)
  }

  async function submit() {
    if (!cart.length || busy) return
    setBusy(true)
    try {
      const body = await post({
        action: 'order',
        items: cart.map(l => ({ product_id: l.product_id, qty: l.qty, modifier_option_ids: l.modifier_option_ids, notes: l.notes })),
      })
      setData(d => ({ ...d, ...body }))
      setConfirmed(body.order_number)
      setCart([]); setView('tab'); setError(null)
    } catch (e) {
      setError(e.message === 'Failed to fetch' ? 'No connection. Your order was NOT placed. Try again.' : e.message)
    }
    setBusy(false)
  }

  async function call(kind, tip_amount) {
    setBusy(true)
    try {
      const body = await post({ action: 'call', kind, tip_amount })
      setData(d => ({ ...d, ...body }))
      setBillOpen(false)
      setFlash(kind === 'bill' ? 'The bill is on its way.' : 'Someone is coming over.')
      setTimeout(() => setFlash(null), 4000)
    } catch (e) { setError(e.message) }
    setBusy(false)
  }

  const cartTotal = cart.reduce((s, l) => s + l.unit * l.qty, 0)

  if (data === undefined) return <div className="order-wrap" style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}><span className="spinner" /></div>
  if (error && !data) return (
    <div className="order-wrap" style={{ textAlign: 'center', paddingTop: 80 }}>
      <img src="/lightlogo-v2.png" alt="Wranglr" style={{ height: 28, marginBottom: 16 }} />
      <div className="card" style={{ padding: 24 }}><p style={{ margin: 0 }}>{error}</p></div>
    </div>
  )

  /* ---- ask who this is, before anything else ---- */
  if (!joined) {
    return (
      <div className="order-wrap">
        <header className="order-head">
          <img src={document.documentElement.dataset.theme === 'dark' ? '/darklogo-v2.png' : '/lightlogo-v2.png'} alt="Wranglr" />
          <h1>{data.business.name}</h1>
          <p>{data.table.name}</p>
        </header>
        <form className="card" style={{ padding: 22 }} onSubmit={join}>
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>Who is ordering?</h2>
          <p style={{ fontSize: '0.87rem', color: 'var(--text-secondary)' }}>
            Everyone at this table shares one tab. Your name goes on what you order,
            so the kitchen knows whose is whose.
          </p>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 0 }}>
            Your name goes to the kitchen and is not used for marketing or shared with anyone else.
          </p>
          {(data.guests ?? []).length > 0 && (
            <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>
              Already here: {(data.guests ?? []).map(g => g.guest_name).join(', ')}
            </p>
          )}
          <input className="input" autoFocus placeholder="Your first name" maxLength={60}
            value={nameDraft} onChange={e => setNameDraft(e.target.value)} />
          <button className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 12 }} disabled={!nameDraft.trim() || busy}>
            {busy ? <span className="spinner" /> : 'Start ordering'}
          </button>
        </form>
      </div>
    )
  }

  /* ---- two-tier menu navigation ---- */
  const menu = data.menu ?? []
  const inGroup = (p) => group === 'all' ? true : group === 'bar' ? p.is_alcohol : !p.is_alcohol
  const groupItems = menu.filter(inGroup)
  const cats = (data.categories ?? []).filter(c => groupItems.some(p => p.category_id === c.id))
  const shown = cat === 'all' ? groupItems : groupItems.filter(p => p.category_id === cat)
  const uncategorised = shown.filter(p => !p.category_id || !cats.find(c => c.id === p.category_id))
  const hasBar = menu.some(p => p.is_alcohol)
  const openCall = (data.calls ?? []).find(c => c.kind === 'waiter')
  const openBill = (data.calls ?? []).find(c => c.kind === 'bill')

  function pickGroup(g) { setGroup(g); setCat('all'); catRowRef.current?.scrollTo({ left: 0 }) }

  return (
    <div className="order-wrap">
      <header className="order-head">
        <img src={document.documentElement.dataset.theme === 'dark' ? '/darklogo-v2.png' : '/lightlogo-v2.png'} alt="Wranglr" />
        <h1>{data.business.name}</h1>
        <p>{data.table.name} · ordering as <strong>{name}</strong></p>
      </header>

      <div className="segmented" style={{ marginBottom: 6 }}>
        <button className={view === 'menu' ? 'active' : ''} onClick={() => setView('menu')}>Menu</button>
        <button className={view === 'tab' ? 'active' : ''} onClick={() => { setConfirmed(null); setView('tab') }}>
          Table tab {data.tab_total > 0 ? `· ${money(data.tab_total)}` : ''}
        </button>
      </div>
      {error && <div className="error-banner" style={{ marginBottom: 10 }}>{error}</div>}
      {flash && <div className="card" style={{ padding: 12, marginBottom: 10, textAlign: 'center', borderTop: '3px solid var(--green)' }}>{flash}</div>}

      {view === 'menu' && (
        <>
          {/* sticky so the guest can always change section without scrolling back */}
          <div className="menu-nav">
            {hasBar && (
              <div className="pill-row">
                <button className={`pill ${group === 'all' ? 'active' : ''}`} onClick={() => pickGroup('all')}>All</button>
                <button className={`pill ${group === 'food' ? 'active' : ''}`} onClick={() => pickGroup('food')}>Food</button>
                <button className={`pill ${group === 'bar' ? 'active' : ''}`} onClick={() => pickGroup('bar')}>Drinks &amp; Bar</button>
              </div>
            )}
            {cats.length > 1 && (
              <div className="pill-row sub" ref={catRowRef}>
                <button className={`pill ${cat === 'all' ? 'active' : ''}`} onClick={() => setCat('all')}>All</button>
                {cats.map(c => (
                  <button key={c.id} className={`pill ${cat === c.id ? 'active' : ''}`} onClick={() => setCat(c.id)}>{c.name}</button>
                ))}
              </div>
            )}
          </div>

          {cats.filter(c => cat === 'all' || c.id === cat).map(c => {
            const items = shown.filter(p => p.category_id === c.id)
            if (!items.length) return null
            return (
              <section key={c.id}>
                <h2 className="cat-title">{c.name}</h2>
                <div style={{ display: 'grid', gap: 8 }}>
                  {items.map(p => <MenuRow key={p.id} p={p} onAdd={tryAdd} />)}
                </div>
              </section>
            )
          })}
          {uncategorised.length > 0 && (
            <section>
              <h2 className="cat-title">Menu</h2>
              <div style={{ display: 'grid', gap: 8 }}>
                {uncategorised.map(p => <MenuRow key={p.id} p={p} onAdd={tryAdd} />)}
              </div>
            </section>
          )}
          {shown.length === 0 && (
            <div className="card" style={{ padding: 22, textAlign: 'center' }}>Nothing in this section right now.</div>
          )}

          {cart.length > 0 && (
            <section style={{ marginTop: 18 }}>
              <h2 className="cat-title">Your order</h2>
              <div style={{ display: 'grid', gap: 6 }}>
                {cart.map(l => (
                  <div key={l.lineId} className="cart-line">
                    <span style={{ flex: 1 }}>
                      {l.name}
                      {l.modNames.length > 0 && <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{l.modNames.join(', ')}</span>}
                      {l.notes && <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--orange)' }}>{l.notes}</span>}
                    </span>
                    <strong>{money(l.unit)}</strong>
                    <button className="btn btn-ghost btn-sm" onClick={() => setCart(c => c.filter(x => x.lineId !== l.lineId))}>×</button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {view === 'tab' && (
        <>
          {confirmed && (
            <div className="card" style={{ padding: 16, textAlign: 'center', marginBottom: 12, borderTop: '3px solid var(--green)' }}>
              <strong>Order #{confirmed} sent</strong>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>The kitchen has it. It will show below.</p>
            </div>
          )}
          {(data.guests ?? []).length > 0 && (
            <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', margin: '0 0 10px' }}>
              At this table: {(data.guests ?? []).map(g => g.guest_name).join(', ')}
            </p>
          )}
          {(data.orders ?? []).length === 0 ? (
            <div className="card" style={{ padding: 22, textAlign: 'center' }}>
              Nothing on the tab yet. Everyone at {data.table.name} shares this tab, so order away.
            </div>
          ) : (
            <>
              {(data.orders ?? []).map(o => (
                <div key={o.id} className="tab-order">
                  <div className="cart-total-row" style={{ fontWeight: 700 }}>
                    <span>#{o.order_number}{o.customer_name ? ` · ${o.customer_name}` : ''}</span>
                    <span>{money(o.total)}</span>
                  </div>
                  {(o.order_items ?? []).map((i, ix) => (
                    <div key={ix} className="cart-total-row" style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
                      <span>{Number(i.qty)}× {i.name_snapshot}{(i.modifiers ?? []).length ? ` (${i.modifiers.map(m => m.name).join(', ')})` : ''}</span>
                      <span className={`chip ${i.item_status === 'done' ? 'chip-green' : i.item_status === 'preparing' ? 'chip-orange' : 'chip-blue'}`}>
                        {i.item_status === 'pending' ? 'sent' : i.item_status === 'preparing' ? 'making' : 'ready'}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
              <div className="cart-total-row grand" style={{ padding: '10px 4px' }}>
                <span>Table total</span><span>{money(data.tab_total)}</span>
              </div>
            </>
          )}

          <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
            <button className="btn btn-secondary btn-lg" disabled={busy || !!openCall} onClick={() => call('waiter')}>
              {openCall ? 'Waiter called, on the way' : 'Call a waiter'}
            </button>
            <button className="btn btn-secondary btn-lg" disabled={busy || !!openBill || !(data.tab_total > 0)}
              onClick={() => setBillOpen(true)}>
              {openBill ? `Bill requested${openBill.tip_amount ? ` · tip ${money(openBill.tip_amount)}` : ''}` : 'Ask for the bill'}
            </button>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 10 }}>
            The tab stays open until it is paid. Order as much as you like.
          </p>
        </>
      )}

      {view === 'menu' && cart.length > 0 && (
        <div className="bottom-bar">
          <button className="btn btn-primary btn-lg" disabled={busy} onClick={submit}>
            {busy ? <span className="spinner" /> : `Send order · ${money(cartTotal)}`}
          </button>
        </div>
      )}

      {picking && <ModifierSheet item={picking} onClose={() => setPicking(null)} onAdd={(ids, mods, notes) => addItem(picking, ids, mods, notes)} />}
      {billOpen && <BillSheet tabTotal={data.tab_total} busy={busy} onClose={() => setBillOpen(false)} onSend={(tip) => call('bill', tip)} />}
    </div>
  )
}

function MenuRow({ p, onAdd }) {
  const off = p.orderable === false
  return (
    <button className={`menu-item ${off ? 'unavailable' : ''}`} disabled={off} onClick={() => onAdd(p)}>
      <span>
        <strong>{p.name}</strong>
        {p.description && <small>{p.description}</small>}
        {off && <small className="soldout">{p.out_of_stock ? 'Sold out' : 'Not available right now'}</small>}
      </span>
      <span className="price">{money(p.price)}</span>
    </button>
  )
}
